/**
 * Legend definitions for all Atlas layers.
 * Separated from components so rebranding/updating colors only touches this file.
 */

export type LegendType = 'gradient' | 'discrete' | 'lines' | 'fills';

export interface LegendClass {
  color: string;
  label: string;
  width?: number; // for line legends
}

export interface LegendDef {
  type: LegendType;
  label: string;
  unit?: string;
  // For gradient legends — CSS gradient stops (left to right)
  gradient?: string;
  // Qualitative end labels, for gradients whose axis is a dimensionless index
  // stretched per-order (wind): printing raw numbers there would mean nothing.
  minLabel?: string;
  maxLabel?: string;
  // For discrete/lines/fills legends
  classes?: LegendClass[];
}

/**
 * spectral_r gradient (blue → green → yellow → orange → red)
 * Matches TiTiler spectral_r colormap used for MDT
 */
const WIND_RELIEF = 'linear-gradient(to right, #2166ac, #4393c3, #5ba7a0, #5aab6e, #8cc63f, #d4d84a, #f2a13a, #d94a24)';
const SPECTRAL_R = 'linear-gradient(to right, #5e4fa2, #3288bd, #66c2a5, #abdda4, #e6f598, #fee08b, #fdae61, #f46d43, #d53e4f, #9e0142)';

/**
 * Blue-forward gradient for Flow Accumulation (EB-81 v0.2), matching the
 * Regrarians "Flow Velocity" reference. Low accumulation is transparent
 * (removed), so the ramp starts at light blue and deepens to navy.
 */
const FLOW_BLUES = 'linear-gradient(to right, #9ecae1, #4292c6, #2171b5, #08306b)';

export const LAYER_LEGENDS: Record<string, LegendDef> = {
  // --- Terreno Base ---
  mdt: {
    type: 'gradient',
    label: 'MDT',
    unit: 'm',
    gradient: SPECTRAL_R,
  },

  // --- Morfologia ---
  slope: {
    type: 'discrete',
    label: 'Declives',
    classes: [
      { color: '#447B2B', label: '<5°' },
      { color: '#669D1D', label: '5-8°' },
      { color: '#9ED567', label: '8-15°' },
      { color: '#E8A909', label: '15-25°' },
      { color: '#EA531F', label: '25-30°' },
      { color: '#CC2708', label: '30-50°' },
      { color: '#8F1111', label: '>50°' },
    ],
  },
  aspect: {
    type: 'discrete',
    label: 'Exposição Solar',
    classes: [
      { color: '#4142A5', label: 'N' },
      { color: '#4195FF', label: 'NE' },
      { color: '#F6EF0E', label: 'E' },
      { color: '#F5B613', label: 'SE' },
      { color: '#D2240A', label: 'S' },
      { color: '#B48BB4', label: 'SW' },
      { color: '#8026A1', label: 'W' },
      { color: '#2096BE', label: 'NW' },
    ],
  },
  geomorphons: {
    type: 'discrete',
    label: 'Geomorphons',
    classes: [
      { color: '#717171', label: 'Flat' },
      { color: '#53050E', label: 'Summit' },
      { color: '#BA2231', label: 'Ridge' },
      { color: '#D45F20', label: 'Shoulder' },
      { color: '#E5CC5B', label: 'Spur' },
      { color: '#E9E998', label: 'Slope' },
      { color: '#A6BA62', label: 'Hollow' },
      { color: '#115A15', label: 'Footslope' },
      { color: '#698195', label: 'Valley' },
      { color: '#000000', label: 'Pit' },
    ],
  },
  // Continuous since 2026-07-15: the layer is now a worker-baked ramp x relief
  // composite, not 5 discrete classes. Stops mirror _WIND_RAMP in
  // order_tasks.py; keep the two in sync. Labels are qualitative (the stretch is
  // per-order p2..p98 of a dimensionless index, so absolute numbers would mean
  // nothing to the reader).
  windExposition: {
    type: 'gradient',
    label: 'Exp. Vento',
    gradient: WIND_RELIEF,
    minLabel: 'Abrigado',
    maxLabel: 'Exposto',
  },

  // --- Erosão ---
  erosionRisk: {
    type: 'discrete',
    label: 'Erosão',
    classes: [
      { color: '#FFFFB2', label: 'Muito Baixo' },
      { color: '#FED976', label: 'Baixo' },
      { color: '#FD8D3C', label: 'Moderado' },
      { color: '#E31A1C', label: 'Alto' },
      { color: '#800026', label: 'Muito Alto' },
    ],
  },

  // --- Hidrologia ---
  flowAcc: {
    type: 'gradient',
    label: 'Acumulação',
    unit: 'log10',
    gradient: FLOW_BLUES,
  },
  streams: {
    type: 'lines',
    label: 'Linhas de Água',
    classes: [
      { color: '#A6CEE3', width: 1.5, label: '1' },
      { color: '#1F78B4', width: 2, label: '2' },
      { color: '#41B6C4', width: 2.5, label: '3' },
      { color: '#33A02C', width: 3, label: '4' },
      { color: '#6A3D9A', width: 3.5, label: '5' },
      { color: '#000080', width: 4, label: '6+' },
    ],
  },
  ridges: {
    type: 'lines',
    label: 'Festos',
    classes: [
      { color: '#FFEDA0', width: 1.5, label: '1' },
      { color: '#FED976', width: 2, label: '2' },
      { color: '#FEB24C', width: 2.5, label: '3' },
      { color: '#FD8D3C', width: 3, label: '4' },
      { color: '#F03B20', width: 3.5, label: '5' },
      { color: '#BD0026', width: 4, label: '6+' },
    ],
  },
  basins: {
    type: 'fills',
    label: 'Bacias',
    classes: [
      { color: '#efedf5', label: 'menor área' },
      { color: '#9e9ac8', label: 'área média' },
      { color: '#54278f', label: 'maior área' },
    ],
  },
  floodZones: {
    type: 'fills',
    label: 'A. Inundáveis',
    classes: [
      { color: '#D73027', label: 'Elevado' },
      { color: '#FC8D59', label: 'Moderado' },
      { color: '#FEE08B', label: 'Baixo' },
    ],
  },

  // --- Contexto OSM ---
  landuse: {
    type: 'fills',
    label: 'Uso do Solo',
    classes: [
      { color: '#5A8A42', label: 'Floresta' },
      { color: '#D4C878', label: 'Agrícola' },
      { color: '#A0A0A0', label: 'Urbano' },
    ],
  },

  // --- Cadastro ---
  cadastroDgt: {
    type: 'lines',
    label: 'Cadastro DGT',
    classes: [
      { color: '#2a2a2a', width: 1.5, label: 'Parcela' },
    ],
  },
  bupiRgg: {
    type: 'lines',
    label: 'BUPi (RGG)',
    classes: [
      { color: '#6b4f3a', width: 1, label: 'Parcela (tracejado)' },
    ],
  },
  freguesias: {
    type: 'lines',
    label: 'Freguesias',
    classes: [
      { color: '#7c3aed', width: 1.5, label: 'Limite de freguesia' },
    ],
  },
  concelhos: {
    type: 'lines',
    label: 'Concelhos',
    classes: [
      { color: '#7c3aed', width: 3, label: 'Limite de concelho' },
    ],
  },

  // --- Restricoes Legais ---
  ren: {
    type: 'fills',
    label: 'REN',
    classes: [
      { color: '#2d6a4f', label: 'Reserva Ecológica Nacional' },
    ],
  },
  renExclusoes: {
    type: 'lines',
    label: 'REN (Exclusões)',
    classes: [
      { color: '#2d6a4f', width: 1.5, label: 'Exclusões' },
    ],
  },
  ran: {
    type: 'fills',
    label: 'RAN',
    classes: [
      { color: '#fbbf24', label: 'Reserva Agrícola Nacional' },
    ],
  },
};
